import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Box, Typography, styled, useTheme } from '@mui/material';
import TextButton from '../components/TextButton';
import Button from '../components/Button';
import StatusBadge from '../components/StatusBadge';
import SvgIcon from '../components/SvgIcon';
import {
  StatusMenu,
  StatusMenuItem,
  TabSwitchContainer,
  TabSwitchItem,
  TabSwitchWrapper,
} from '../components/FilterToolbarStyles';
import ApplicationsDialog from '../components/ApplicationsDialog';
import ServiceIdentifierDialog from '../components/ServiceIdentifierDialog';
import ExpirationDateDialog from '../components/ExpirationDateDialog';
import FilePreviewDialog from '../components/FilePreviewDialog';
import RemoveCarConfirmationDialog from '../components/drivers/RemoveCarConfirmationDialog';
import { useUsers } from '../hooks/user';
import { useCars } from '../hooks/cars';
import api from '../services/api';
import { showGlobalToast } from '../services/toastBus';
import { SERVICE_OPTIONS, SERVICE_STATUS_VARIANTS } from '../constants/service';
import { FILE_KINDS, FILE_STATUS_VARIANTS, USER_FILES, CAR_FILES } from '../constants/file';
import { THEME_MODES } from '../theme';
import { getThemeColors } from '../helpers/themeStyles';
import {
  ADD_VEHICLE_SLUG,
  PATH,
  pathDriver,
  pathDriverVehicle,
  pathDriverVehicles,
} from '../constants/routes';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import { getFileStatusLabel, normalizeCarStatus, normalizeUserStatus } from '../helpers/status';
import { USER_STATUS_VARIANTS } from '../constants/user';
import { CAR_STATUS_VARIANTS } from '../constants/car';
import DriverDetailsTab from './driver-details/DriverDetailsTab';
import CarDetailsTab from './driver-details/CarDetailsTab';
import CarList from './driver-details/CarList';
import { CAR_PLATE_NUMBER_ERROR_MESSAGE, isValidCarPlateNumber } from '../helpers/plateNumber';

const normalizeCarPowerForCompare = (value) =>
  value === null || value === undefined || value === '' ? '' : String(value);

const normalizeRegistrationDateForCompare = (value) => {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  margin: '0px 32px 32px 0px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 28,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
}));

const Breadcrumb = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 24,
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
});

const TabsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px 24px',
  gap: 16,
});

const mapUserFilesToDocuments = (userFiles, fileTemplates, options = {}) =>
  fileTemplates.map((template) => {
    const userFile = userFiles?.find((uf) => uf.type === template.type);
    const file = userFile?.file;
    return {
      id: userFile?.id ?? template.type,
      type: template.type,
      label: template.title,
      name: file?.name ?? null,
      date: userFile?.expiresAt ?? null,
      status: userFile?.status ?? null,
      empty: !file,
      url: file?.url,
      downloadUrl: file?.downloadUrl,
      expiring: template.expiring ?? false,
      ...(options.includeCarId && userFile?.carId && { carId: userFile.carId }),
    };
  });

const CAR_DOCUMENT_FILES = CAR_FILES.filter((f) => !f.isPhoto);

const DriverDetailsScreen = () => {
  const { userId, carId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = getThemeColors(theme);

  const activeTab = location.pathname.includes(PATH.VEHICLES) ? 'car' : 'driver';
  const selectedCarId = carId || null;
  const { data: users, refetch: refetchUsers } = useUsers();
  const { data: cars, loading: carsLoading, refetch: refetchCars } = useCars(userId);
  const [userFiles, setUserFiles] = useState([]);
  const [carFiles, setCarFiles] = useState([]);
  const [uploadingType, setUploadingType] = useState(null);
  const fileInputRef = useRef(null);
  const pendingUploadRef = useRef(null);
  const [applicationsDialogOpen, setApplicationsDialogOpen] = useState(false);
  const [serviceIdentifierTarget, setServiceIdentifierTarget] = useState(null);
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusDoc, setStatusDoc] = useState(null);
  const [carToRemove, setCarToRemove] = useState(null);
  const [pendingCarFiles, setPendingCarFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [savingDriver, setSavingDriver] = useState(false);
  const [savingCar, setSavingCar] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    city: '',
    birthDate: '',
    status: USER_STATUS_VARIANTS.pending,
    contributionPercentage: '15',
  });
  const [carFormData, setCarFormData] = useState({
    id: null,
    brand: '',
    model: '',
    color: '',
    plateNumber: '',
    power: '',
    registrationDate: '',
    fuelType: 'GASOLINE',
    transmission: 'AUTOMATIC',
    status: CAR_STATUS_VARIANTS.active,
  });
  const [carFormErrors, setCarFormErrors] = useState({
    brand: '',
    model: '',
    color: '',
    registrationDate: '',
    plateNumber: '',
    power: '',
  });

  const user = users?.find((u) => String(u.id) === String(userId));

  const hasFormChanged = user
    ? formData.name !== (user.name || '') ||
      formData.email !== (user.email || '') ||
      formData.mobileNumber !== (user.mobileNumber || '') ||
      formData.city !== (user.city || '') ||
      formData.birthDate !== (user.birthDate || '') ||
      formData.status !== normalizeUserStatus(user) ||
      Number(formData.contributionPercentage) !== Number(user.contributionPercentage ?? 15)
    : false;

  const selectedCar = useMemo(
    () =>
      selectedCarId && cars?.length
        ? cars.find((c) => String(c.id) === String(selectedCarId))
        : null,
    [selectedCarId, cars]
  );

  const hasCarFormChanged = useMemo(() => {
    if (carFormData.id) {
      if (!selectedCar) return false;

      const orig = {
        brand: selectedCar.brand || '',
        model: selectedCar.model || '',
        color: selectedCar.color || '',
        plateNumber: selectedCar.plateNumber || '',
        power: normalizeCarPowerForCompare(selectedCar.power),
        registrationDate: normalizeRegistrationDateForCompare(selectedCar.date || ''),
        fuelType: selectedCar.fuelType || 'GASOLINE',
        transmission: selectedCar.transmission || 'AUTOMATIC',
        status: normalizeCarStatus(selectedCar),
      };

      return (
        carFormData.brand !== orig.brand ||
        carFormData.model !== orig.model ||
        carFormData.color !== orig.color ||
        carFormData.plateNumber !== orig.plateNumber ||
        normalizeCarPowerForCompare(carFormData.power) !== orig.power ||
        normalizeRegistrationDateForCompare(carFormData.registrationDate) !==
          orig.registrationDate ||
        carFormData.fuelType !== orig.fuelType ||
        carFormData.transmission !== orig.transmission ||
        carFormData.status !== orig.status
      );
    }
    return (
      !!carFormData.brand ||
      !!carFormData.model ||
      !!carFormData.color ||
      !!carFormData.plateNumber ||
      !!carFormData.power ||
      !!carFormData.registrationDate ||
      pendingCarFiles.length > 0
    );
  }, [carFormData, selectedCar, pendingCarFiles]);

  const verifiedServices = user?.services?.filter(
    (s) => s.status === SERVICE_STATUS_VARIANTS.verified
  );

  const driverDocuments = useMemo(
    () => mapUserFilesToDocuments(userFiles, USER_FILES.personal),
    [userFiles]
  );

  const drivioDocuments = useMemo(
    () => mapUserFilesToDocuments(userFiles, USER_FILES.system),
    [userFiles]
  );

  const selectedCarFiles = useMemo(
    () => (selectedCarId ? carFiles.filter((f) => String(f.carId) === String(selectedCarId)) : []),
    [carFiles, selectedCarId]
  );

  const selectedCarDocuments = useMemo(
    () => mapUserFilesToDocuments(selectedCarFiles, CAR_DOCUMENT_FILES, { includeCarId: true }),
    [selectedCarFiles]
  );

  const pendingCarFilesAsUserFiles = useMemo(
    () =>
      pendingCarFiles.map((f) => ({
        id: f.id,
        type: f.type,
        file: { name: f.name, url: f.url, downloadUrl: f.downloadUrl },
        expiresAt: f.expiresAt,
        status: f.status ?? FILE_STATUS_VARIANTS.PENDING,
      })),
    [pendingCarFiles]
  );

  const newCarDocuments = useMemo(
    () => mapUserFilesToDocuments(pendingCarFilesAsUserFiles, CAR_DOCUMENT_FILES),
    [pendingCarFilesAsUserFiles]
  );

  const newCarPhotoDoc = useMemo(() => {
    const template = CAR_FILES.find((f) => f.isPhoto);
    const pending = pendingCarFiles.find((f) => f.type === template?.type);

    return {
      id: pending?.id ?? template?.type,
      type: template?.type,
      label: template?.title,
      name: pending?.name ?? null,
      status: pending?.status ?? FILE_STATUS_VARIANTS.PENDING,
      empty: !pending,
      url: pending?.url ?? null,
      downloadUrl: pending?.downloadUrl ?? null,
    };
  }, [pendingCarFiles]);

  const selectedCarPhotoDoc = useMemo(() => {
    const template = CAR_FILES.find((f) => f.isPhoto);
    const userFile = selectedCarFiles?.find((uf) => uf.type === template?.type);
    const file = userFile?.file;
    return {
      id: userFile?.id ?? template?.type,
      type: template?.type,
      label: template?.title,
      name: file?.name ?? null,
      status: userFile?.status ?? FILE_STATUS_VARIANTS.PENDING,
      empty: !file,
      url: file?.url,
      downloadUrl: file?.downloadUrl ?? null,
      ...(userFile?.carId && { carId: userFile.carId }),
    };
  }, [selectedCarFiles]);

  useEffect(() => {
    setCarFormErrors({
      brand: '',
      model: '',
      color: '',
      registrationDate: '',
      plateNumber: '',
      power: '',
    });
    if (carId === ADD_VEHICLE_SLUG) {
      setCarFormData({
        id: null,
        brand: '',
        model: '',
        color: '',
        plateNumber: '',
        power: '',
        registrationDate: '',
        fuelType: 'GASOLINE',
        transmission: 'AUTOMATIC',
        status: CAR_STATUS_VARIANTS.active,
      });
      setPendingCarFiles([]);
    } else if (carId && cars?.length) {
      const car = cars.find((c) => String(c.id) === String(carId));

      if (car) {
        setCarFormData({
          id: car.id,
          brand: car.brand || '',
          model: car.model || '',
          color: car.color || '',
          plateNumber: car.plateNumber || '',
          power: car.power ? String(car.power) : '',
          registrationDate: car.date || '',
          fuelType: car.fuelType || 'GASOLINE',
          transmission: car.transmission || 'AUTOMATIC',
          status: normalizeCarStatus(car),
        });
        setPendingCarFiles([]);
      }
    } else {
      setCarFormData({
        id: null,
        brand: '',
        model: '',
        color: '',
        plateNumber: '',
        power: '',
        registrationDate: '',
        fuelType: 'GASOLINE',
        transmission: 'AUTOMATIC',
        status: CAR_STATUS_VARIANTS.active,
      });
    }
  }, [carId, cars]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        mobileNumber: user.mobileNumber || '',
        city: user.city || '',
        birthDate: user.birthDate || '',
        status: normalizeUserStatus(user),
        contributionPercentage: String(user.contributionPercentage ?? 15),
      });
    }
  }, [user]);

  const fetchUserFiles = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await api.get('/files/user', { params: { userId } });
      const allFiles = response?.ok && response.data?.data ? response.data.data : [];
      const nextUserFiles = allFiles.filter((f) => f.kind !== FILE_KINDS.CAR);
      setUserFiles(nextUserFiles);
    } catch {
      // Keep previous state on error to avoid flashing empty while refetching
    }
  }, [userId]);

  useEffect(() => {
    const filesFromCars = cars.flatMap((car) =>
      (car.files || []).map((f) => ({ ...f, carId: car.id, kind: FILE_KINDS.CAR }))
    );
    setCarFiles(filesFromCars);
  }, [cars]);

  useEffect(() => {
    fetchUserFiles();
  }, [fetchUserFiles]);

  const handleUploadClick = useCallback((doc) => {
    pendingUploadRef.current = {
      kind: FILE_KINDS.USER,
      type: doc.type,
      expiring: doc.expiring ?? false,
    };
    fileInputRef.current?.click();
  }, []);

  const handleCarUploadClick = useCallback(
    (doc) => {
      pendingUploadRef.current = {
        kind: FILE_KINDS.CAR,
        type: doc.type,
        expiring: doc.expiring ?? false,
        carId: doc.carId ?? carFormData.id,
      };
      fileInputRef.current?.click();
    },
    [carFormData.id]
  );

  const performUpload = useCallback(
    async (file, pending, expiresAt = null) => {
      if (!file || !pending || !userId) return;

      const formData = new FormData();

      formData.append('file', file);

      if (!(pending.kind === FILE_KINDS.CAR && !pending.carId)) {
        formData.append('kind', pending.kind);
      }

      formData.append('type', pending.type);

      formData.append('userId', userId);

      if (pending.kind === FILE_KINDS.CAR && pending.carId) {
        formData.append('carId', pending.carId);
      }

      if (expiresAt) {
        formData.append(
          'expiresAt',
          expiresAt instanceof Date ? expiresAt.toISOString() : expiresAt
        );
      }

      const response = await api.post('/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response?.ok) {
        const createdFile = response.data?.data ?? response.data;
        const isNewCarUpload = pending.kind === FILE_KINDS.CAR && !pending.carId;

        if (isNewCarUpload && createdFile?.id) {
          const fileData = createdFile.file ?? createdFile;

          setPendingCarFiles((prev) => {
            const filtered = prev.filter((f) => f.type !== pending.type);

            return [
              ...filtered,
              {
                id: createdFile.id,
                type: pending.type,
                name: fileData?.name ?? createdFile.name ?? file.name,
                url: fileData?.url ?? createdFile.url,
                downloadUrl: fileData?.downloadUrl ?? createdFile.downloadUrl,
                status: createdFile?.status ?? FILE_STATUS_VARIANTS.PENDING,
                expiresAt:
                  createdFile.expiresAt ??
                  (expiresAt
                    ? expiresAt instanceof Date
                      ? expiresAt.toISOString()
                      : expiresAt
                    : null),
                expiring: pending.expiring ?? false,
              },
            ];
          });
        } else {
          await fetchUserFiles();

          if (pending.kind === FILE_KINDS.CAR) await refetchCars();
        }
      }
    },
    [userId, fetchUserFiles, refetchCars]
  );

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      const pending = pendingUploadRef.current;
      event.target.value = '';

      if (!file || !pending || !userId) return;

      if (pending.expiring) {
        setPendingFile({ file, pending });
        setExpirationDialogOpen(true);
        pendingUploadRef.current = null;
      } else {
        setUploadingType(pending.type);
        pendingUploadRef.current = null;
        performUpload(file, pending).finally(() => setUploadingType(null));
      }
    },
    [userId, performUpload]
  );

  const handleExpirationSave = useCallback(
    async (selectedDate) => {
      if (!pendingFile) return;

      const { file, pending } = pendingFile;

      setUploadingType(pending.type);

      try {
        await performUpload(file, pending, selectedDate);
      } finally {
        setUploadingType(null);
      }
    },
    [pendingFile, performUpload]
  );

  const handleExpirationClose = useCallback(() => {
    setExpirationDialogOpen(false);
    setPendingFile(null);
  }, []);

  const handleRemove = useCallback(
    async (id) => {
      const response = await api.delete(`/files/${id}`);

      if (response?.ok) {
        await fetchUserFiles();
        await refetchCars();
      }
    },
    [fetchUserFiles, refetchCars]
  );

  const handleRemoveCarFile = useCallback(
    (id) => {
      const isPending = pendingCarFiles.some((f) => String(f.id) === String(id));

      if (isPending) {
        setPendingCarFiles((prev) => prev.filter((f) => String(f.id) !== String(id)));
      } else {
        handleRemove(id);
      }
    },
    [pendingCarFiles, handleRemove]
  );

  const statusMenuOpen = Boolean(statusAnchorEl);

  const handleStatusClick = useCallback((event, doc) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusDoc(doc);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
    setStatusDoc(null);
  }, []);

  const handleStatusSelect = useCallback(
    async (newStatus) => {
      if (!statusDoc?.id) return;

      // For non-created car: update status locally only
      if (!carFormData.id) {
        const isPending = pendingCarFiles.some((f) => String(f.id) === String(statusDoc.id));

        if (isPending) {
          setPendingCarFiles((prev) =>
            prev.map((f) =>
              String(f.id) === String(statusDoc.id) ? { ...f, status: newStatus } : f
            )
          );

          handleStatusMenuClose();
          return;
        }
      }

      const response = await api.patch(`/files/${statusDoc.id}/status`, {
        status: newStatus,
      });

      if (!response?.ok) return;

      handleStatusMenuClose();

      await fetchUserFiles();
      await refetchCars();
    },
    [statusDoc, carFormData.id, pendingCarFiles, fetchUserFiles, refetchCars, handleStatusMenuClose]
  );

  const handleBack = useCallback(() => {
    navigate(PATH.DRIVERS);
  }, [navigate]);

  const handleTabChange = useCallback(
    (tab) => {
      if (!userId) return;

      if (tab === 'driver') {
        navigate(pathDriver(userId));
      } else {
        navigate(pathDriverVehicles(userId));
      }
    },
    [userId, navigate]
  );

  const handleSelectCar = useCallback(
    (car) => {
      if (!userId) return;

      navigate(pathDriverVehicle(userId, car.id));
    },
    [userId, navigate]
  );

  const handleBackToCarList = useCallback(() => {
    if (!userId) return;

    navigate(pathDriverVehicles(userId));
  }, [userId, navigate]);

  const handleAddCar = useCallback(() => {
    if (!userId) return;

    navigate(pathDriverVehicle(userId, ADD_VEHICLE_SLUG));
  }, [userId, navigate]);

  const handleRemoveCarClick = useCallback((car) => {
    setCarToRemove(car);
  }, []);

  const handleRemoveCarConfirm = useCallback(async () => {
    if (!carToRemove?.id || !userId) return;

    const response = await api.delete(`/cars/${carToRemove.id}`);

    if (response?.ok) {
      await refetchCars();

      setCarToRemove(null);

      navigate(pathDriverVehicles(userId));
    }
  }, [carToRemove, userId, refetchCars, navigate]);

  const handleRemoveCarClose = useCallback(() => {
    setCarToRemove(null);
  }, []);

  const handleFileClick = useCallback((doc) => {
    if (doc?.url && !doc.empty) {
      setPreviewFile({
        url: doc.url,
        downloadUrl: doc.downloadUrl,
        name: doc.name,
        fileName: doc.name,
      });
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!userId) return;

    const contributionPercentage = Number(formData.contributionPercentage);
    if (
      formData.contributionPercentage === '' ||
      Number.isNaN(contributionPercentage) ||
      contributionPercentage < 0 ||
      contributionPercentage > 100
    ) {
      showGlobalToast(
        'Le pourcentage de contribution doit être un nombre entre 0 et 100.',
        'error'
      );
      return;
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      mobileNumber: formData.mobileNumber || null,
      city: formData.city || null,
      birthDate: formData.birthDate || null,
      status: formData.status,
      contributionPercentage,
    };

    setSavingDriver(true);
    try {
      const response = await api.put(`/users/${userId}`, payload);

      if (response?.ok) {
        showGlobalToast('Informations du chauffeur enregistrées.', 'success');
        await refetchUsers();
      }
    } finally {
      setSavingDriver(false);
    }
  }, [userId, formData, refetchUsers]);

  const validateCarForm = useCallback(() => {
    const plateNumber = carFormData.plateNumber?.trim();

    const errors = {
      brand: !carFormData.brand?.trim() ? 'Ce champ est requis' : '',
      model: !carFormData.model?.trim() ? 'Ce champ est requis' : '',
      color: !carFormData.color?.trim() ? 'Ce champ est requis' : '',
      registrationDate: !carFormData.registrationDate?.trim() ? 'Ce champ est requis' : '',
      plateNumber:
        plateNumber && !isValidCarPlateNumber(plateNumber) ? CAR_PLATE_NUMBER_ERROR_MESSAGE : '',
      power: '',
    };

    setCarFormErrors(errors);

    return !Object.values(errors).some(Boolean);
  }, [carFormData]);

  const handleCarSave = useCallback(async () => {
    if (!userId || !validateCarForm()) return;

    const payload = {
      brand: carFormData.brand || null,
      model: carFormData.model || null,
      color: carFormData.color || null,
      plateNumber: carFormData.plateNumber?.trim() || null,
      power: carFormData.power?.trim() || null,
      date: carFormData.registrationDate || null,
      fuelType: carFormData.fuelType || 'GASOLINE',
      transmission: carFormData.transmission || 'AUTOMATIC',
      status: carFormData.status ?? CAR_STATUS_VARIANTS.active,
    };

    setSavingCar(true);
    try {
      let response;
      if (carFormData.id) {
        response = await api.put(`/cars/${carFormData.id}`, payload);
      } else {
        const files = pendingCarFiles.map((f) => ({
          id: f.id,
          status: f.status ?? FILE_STATUS_VARIANTS.PENDING,
          type: f.type,
        }));

        response = await api.post('/cars', {
          ...payload,
          userId,
          files,
        });
      }

      if (response?.ok) {
        showGlobalToast(carFormData.id ? 'Véhicule mis à jour.' : 'Véhicule ajouté.', 'success');
        if (!carFormData.id) setPendingCarFiles([]);

        await refetchCars();

        if (!carFormData.id) handleBackToCarList();
      }
    } finally {
      setSavingCar(false);
    }
  }, [userId, carFormData, pendingCarFiles, refetchCars, handleBackToCarList, validateCarForm]);

  const isExpiringSoon = useCallback((date) => {
    if (!date) return false;
    const expiresAt = new Date(date);
    const now = new Date();
    const daysUntilExpiry = (expiresAt - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  }, []);

  if (!user && users?.length) {
    return (
      <Body>
        <Box sx={{ padding: 24, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Chauffeur introuvable</Typography>
          <TextButton onClick={handleBack} sx={{ mt: 2 }}>
            Retour aux chauffeurs
          </TextButton>
        </Box>
      </Body>
    );
  }

  if (!user) {
    return (
      <Body>
        <Box sx={{ padding: 24, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Chargement...</Typography>
        </Box>
      </Body>
    );
  }

  return (
    <Body>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Breadcrumb onClick={activeTab === 'car' && selectedCarId ? handleBackToCarList : handleBack}>
        <SvgIcon icon={ArrowLeftIcon} color={colors.textSecondary} />
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
          {activeTab === 'car' && selectedCarId ? 'VÉHICULES' : 'CHAUFFEURS'}
        </Typography>
      </Breadcrumb>

      <TabsRow>
        <TabSwitchWrapper>
          <TabSwitchContainer>
            <TabSwitchItem
              $active={activeTab === 'driver'}
              onClick={() => handleTabChange('driver')}
            >
              INFORMATIONS CHAUFFEUR
            </TabSwitchItem>
            <TabSwitchItem $active={activeTab === 'car'} onClick={() => handleTabChange('car')}>
              INFORMATIONS VÉHICULE
            </TabSwitchItem>
          </TabSwitchContainer>
        </TabSwitchWrapper>
        {activeTab === 'car' && (
          <Button variant="contained" color="primary" onClick={handleAddCar}>
            Ajouter un véhicule
          </Button>
        )}
      </TabsRow>

      {activeTab === 'driver' && (
        <DriverDetailsTab
          formData={formData}
          setFormData={setFormData}
          hasFormChanged={hasFormChanged}
          isSaving={savingDriver}
          verifiedServices={verifiedServices}
          driverDocuments={driverDocuments}
          drivioDocuments={drivioDocuments}
          onUploadClick={handleUploadClick}
          onFileClick={handleFileClick}
          onRemove={handleRemove}
          onStatusClick={handleStatusClick}
          onSave={handleSave}
          onOpenApplications={() => setApplicationsDialogOpen(true)}
          onServiceIdentifierClick={(svc) => setServiceIdentifierTarget(svc)}
          uploadingType={uploadingType}
        />
      )}

      {activeTab === 'car' &&
        ((!carsLoading && !cars.length) || selectedCarId ? (
          <CarDetailsTab
            carFormData={carFormData}
            setCarFormData={setCarFormData}
            carFormErrors={carFormErrors}
            setCarFormErrors={setCarFormErrors}
            carDocuments={carFormData.id ? selectedCarDocuments : newCarDocuments}
            carPhotoDoc={carFormData.id ? selectedCarPhotoDoc : newCarPhotoDoc}
            onCarUploadClick={handleCarUploadClick}
            onFileClick={handleFileClick}
            onRemove={handleRemoveCarFile}
            onStatusClick={handleStatusClick}
            onSave={handleCarSave}
            onRemoveCar={selectedCarId ? handleRemoveCarClick : undefined}
            uploadingType={uploadingType}
            isExpiringSoon={isExpiringSoon}
            hasCarFormChanged={hasCarFormChanged}
            isSaving={savingCar}
          />
        ) : (
          <CarList
            cars={cars}
            carFiles={carFiles}
            onSelectCar={handleSelectCar}
            onRemoveCar={handleRemoveCarClick}
          />
        ))}

      <ApplicationsDialog
        open={applicationsDialogOpen}
        onClose={() => setApplicationsDialogOpen(false)}
        services={user?.services ?? []}
        userId={user?.id}
        onAttachSuccess={refetchUsers}
        onRemoveSuccess={refetchUsers}
        onStatusChange={refetchUsers}
      />

      <ServiceIdentifierDialog
        open={!!serviceIdentifierTarget}
        onClose={() => setServiceIdentifierTarget(null)}
        service={serviceIdentifierTarget}
        platformLabel={
          serviceIdentifierTarget
            ? (SERVICE_OPTIONS.find((o) => o.key === serviceIdentifierTarget.platform)?.label ?? '')
            : ''
        }
        onSaved={refetchUsers}
      />

      <ExpirationDateDialog
        open={expirationDialogOpen}
        onClose={handleExpirationClose}
        fileName={pendingFile?.file?.name ?? ''}
        onSave={handleExpirationSave}
        saving={!!uploadingType}
      />

      <FilePreviewDialog
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      <RemoveCarConfirmationDialog
        open={!!carToRemove}
        onClose={handleRemoveCarClose}
        onConfirm={handleRemoveCarConfirm}
        carName={
          carToRemove
            ? [carToRemove.brand ?? carToRemove.marque, carToRemove.model ?? carToRemove.modele]
                .filter(Boolean)
                .join(' ')
            : null
        }
      />

      {!!statusDoc && (
        <StatusMenu
          anchorEl={statusAnchorEl}
          open={statusMenuOpen}
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleStatusMenuClose();
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {[
            FILE_STATUS_VARIANTS.PENDING,
            FILE_STATUS_VARIANTS.VERIFIED,
            FILE_STATUS_VARIANTS.EXPIRED,
            FILE_STATUS_VARIANTS.REJECTED,
          ]
            .filter((status) => status !== statusDoc.status)
            .map((status) => (
              <StatusMenuItem key={status} onClick={() => handleStatusSelect(status)} disableRipple>
                <StatusBadge variant={status}>{getFileStatusLabel(status)}</StatusBadge>
              </StatusMenuItem>
            ))}
        </StatusMenu>
      )}
    </Body>
  );
};

export default DriverDetailsScreen;
